import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, type Path, type Resolver, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import type { ProductInput } from "@nh/shared";
import { Alert, Form, FormError } from "@/shared/ui/core";
import { FormWizard, useStepFields, useWizard, useWizardEnter } from "@/shared/ui/core";
import { PageSkeleton } from "@/shared/ui/ui";
import { PageHeader } from "@/shared/ui/layout";
import { isApiError } from "@/shared/api/client";
import { AdminGuard } from "@/features/admin/components";
import { ProductDetailWizardBody } from "./components/ProductDetailWizardBody";
import { ProductFormActions } from "./components/ProductFormActions";
import { ProductTabs } from "./components/ProductTabs";
import {
  emptyProductFormValues,
  productFormSchema,
  productToFormValues,
  toProductUpdate,
  type ProductFormValues,
} from "./productForm";
import {
  detailStepOfField,
  fieldsOfDetailStep,
  PRODUCT_DETAIL_STEPS,
} from "./productDetailSteps";
import { useAdminProduct, useUpdateProduct } from "./products.queries";
import { useApiErrorMessage } from "./useApiErrorMessage";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Los DETALLES de un producto: la ficha larga, en cuatro pasos.
 *
 * ── Por qué es una pantalla aparte y no un paso más ─────────────────
 * Son once bloques y más de sesenta campos, todos opcionales, y su
 * contenido no se escribe el día que se da de alta el producto: se
 * escribe cuando hay tiempo, y a menudo lo escribe otra persona. Metidos
 * en el asistente de alta convertían "publicar un producto" en un
 * trabajo de una tarde. Aquí se rellenan cuando toque, sobre un producto
 * que ya está en el catálogo.
 *
 * ── Solo al EDITAR, y por eso no hay ruta de alta ───────────────────
 * Esta pantalla necesita un producto que exista: es la segunda pestaña
 * de su ficha (`ProductTabs`). No hay `/admin/products/new/detalles`
 * porque no habría nada que detallar.
 *
 * ── UN formulario, y el producto ENTERO ─────────────────────────────
 * El formulario se llena con `productToFormValues(producto)` completo,
 * aunque solo se pinte el editorial. Es lo que hace que guardar aquí no
 * toque nada más: el PATCH sale de los MISMOS valores que ya tenía el
 * producto, con el editorial cambiado. Un formulario recortado al
 * editorial obligaría a componer a mano un PATCH parcial, y la primera
 * vez que alguien añadiera un campo al contrato, guardar los detalles se
 * llevaría por delante ese campo sin que nada fallara.
 *
 * Y como el esquema que valida es el mismo (`productFormSchema`), un
 * producto que no valida —uno importado sin precio, digamos— avisa aquí
 * en vez de guardarse a medias.
 *
 * ── Se puede guardar desde cualquier paso ───────────────────────────
 * Media ficha es mejor que una plantilla con huecos. Ningún bloque es
 * obligatorio, así que "Guardar detalles" está en los cuatro pasos y
 * guarda lo que haya.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminProductDetailsPage() {
  return (
    <AdminGuard>
      <ProductDetailsScreen />
    </AdminGuard>
  );
}

function ProductDetailsScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const existing = useAdminProduct(id);
  const update = useUpdateProduct(id ?? "");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  /*
   * Si el asistente se pintara antes del `reset`, los once bloques
   * nacerían PLEGADOS: cada uno decide si abrirse mirando si tiene algo
   * escrito, y en ese primer render todavía no lo tiene. Quien abre la
   * ficha de un producto ya escrito la vería en blanco hasta tocar cada
   * cabecera. Se espera al reset, que es cosa de un fotograma.
   */
  const [loaded, setLoaded] = useState(false);

  const form = useForm<ProductFormValues, unknown, ProductInput>({
    // El mismo `as` y por el mismo motivo que en el asistente de alta:
    // el esquema DEVUELVE un `ProductInput` tras su `.transform().pipe()`
    // y la varianza de `zodResolver` no lo admite. El comportamiento sí.
    resolver: zodResolver(productFormSchema) as unknown as Resolver<
      ProductFormValues,
      unknown,
      ProductInput
    >,
    defaultValues: emptyProductFormValues(),
    mode: "onSubmit",
  });

  const loadErrorMessage = useApiErrorMessage(existing.error);

  useEffect(() => {
    if (!existing.data) return;
    form.reset(productToFormValues(existing.data));
    setLoaded(true);
  }, [existing.data, form]);

  /*
   * El reparto de campos por paso. Memorizado porque de él cuelgan el
   * foco al cambiar de paso y el salto tras un guardado fallido: una
   * identidad nueva en cada render volvería a disparar los dos efectos.
   */
  const stepFieldsOf = useCallback((step: number) => fieldsOfDetailStep(step), []);

  const { validateStep, focusStepError, firstStepWithError } = useStepFields(
    form,
    stepFieldsOf,
    PRODUCT_DETAIL_STEPS.length,
  );

  const wizard = useWizard({
    total: PRODUCT_DETAIL_STEPS.length,
    canAdvance: validateStep,
  });

  /**
   * Guarda y SE QUEDA.
   *
   * Al contrario que el alta, que termina en el listado: aquí se está
   * escribiendo una pieza larga, y lo normal es guardar a mitad para no
   * perderlo y seguir en el mismo bloque. Sacar a quien escribe de la
   * pantalla en cada guardado sería castigar la costumbre que se quiere.
   */
  const submit: SubmitHandler<ProductInput> = async (input) => {
    setSubmitError(null);
    setSaved(false);
    try {
      await update.mutateAsync(toProductUpdate(input));
      setSaved(true);
    } catch (error) {
      /*
       * Cuando el servidor señala campos, el aviso va EN el campo. Y si
       * el campo es de OTRA pantalla —el asistente de alta— `stepOfField`
       * devuelve -1 y no se salta a ningún sitio: el mensaje general de
       * abajo es entonces lo único que se puede decir con honestidad.
       */
      if (isApiError(error) && error.fields) {
        let firstStep = Number.POSITIVE_INFINITY;
        let outsider: string | null = null;

        for (const [field, messages] of Object.entries(error.fields)) {
          const message = messages[0];
          if (!message) continue;
          form.setError(field as Path<ProductFormValues>, { type: "server", message });
          const step = detailStepOfField(field);
          if (step >= 0) firstStep = Math.min(firstStep, step);
          else outsider ??= message;
        }

        if (Number.isFinite(firstStep)) {
          wizard.goTo(firstStep);
          return;
        }
        setSubmitError(
          outsider ?? (isApiError(error) ? error.message : t("Revisa los datos")),
        );
        return;
      }

      setSubmitError(
        isApiError(error) ? error.message : t("Algo salió mal. Vuelve a intentarlo."),
      );
    }
  };

  const handleKeyDown = useWizardEnter(wizard);

  if (existing.isPending || (!loaded && !existing.isError)) return <PageSkeleton />;

  if (existing.isError || !id) {
    return (
      <Alert variant="error" title={t("No se pudo cargar el producto")}>
        {loadErrorMessage}
      </Alert>
    );
  }

  const name = existing.data?.translations.es.name ?? "";

  return (
    <>
      {/* Las pestañas, en la fila del título. Ver `AdminProductFormPage`. */}
      <PageHeader
        compact
        title={name || t("Detalles del producto")}
        actions={<ProductTabs id={id} active="detalles" />}
      />

      <FormError error={submitError} onDismiss={() => setSubmitError(null)} />

      {/*
        El "ya está guardado" es un aviso y no una navegación: quien
        escribe sigue en el mismo bloque. Se apaga solo al volver a
        enviar, que es cuando deja de ser cierto.
      */}
      {saved && (
        <Alert variant="success" onDismiss={() => setSaved(false)}>
          {t("Detalles guardados.")}
        </Alert>
      )}

      <Form form={form} onSubmit={submit} onKeyDown={handleKeyDown}>
        <FormWizard
          steps={PRODUCT_DETAIL_STEPS}
          wizard={wizard}
          progressLabel="Progreso de los detalles"
          focusStepError={focusStepError}
          submitCount={form.formState.submitCount}
          firstStepWithError={firstStepWithError}
          actions={
            <ProductFormActions
              submitLabel={t("Guardar detalles")}
              onCancel={() => navigate("/admin/products")}
              onBack={wizard.isFirst ? undefined : wizard.back}
              onNext={wizard.isLast ? undefined : () => void wizard.next()}
            />
          }
        >
          <ProductDetailWizardBody step={wizard.step} />
        </FormWizard>
      </Form>
    </>
  );
}
